import { buildDeepLinkUrl } from "@common/utils/navigationUtils"
import { APP_URL } from "@data/constants/apiConstants"
import { switchAccount } from "@data/redux/actions/authTokens"
import { setPendingDeepLink } from "@data/redux/actions/notifications"
import { setIsParentMode } from "@data/redux/actions/parentMode"
import { selectSessionStoredTokens } from "@data/utility/familyUtils"
import { useCallback } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

/** Le compte de famille d'un identifiant, dans la réserve de jetons. */
export const findStoredAccount = (storedTokens, userId) =>
    (storedTokens ?? []).find((stored) => stored?.userId != null && String(stored.userId) === String(userId))

/**
 * Peut-on quitter l'espace parent vers l'espace de l'enfant affiché ?
 *
 * Deux refus, chacun repris de la bascule de profil existante : le compte doit porter un jeton
 * (`handleExitToChild` : `if (account.token)`, et `handleUserSelection` sort carrément sans lui),
 * et l'impersonation verrouille la pastille de profil (`ParentTabHeader` : `profileDisabled`).
 */
export function canExitToChild(user, storedTokens) {
    if (user?.impersonation) return false
    return !!findStoredAccount(storedTokens, user?.id)?.token
}

/**
 * useJoinLiveAsChild — faire entrer son enfant en live, depuis l'espace parent.
 *
 * Le parent qui voit la séance du jour doit aujourd'hui trouver seul la bascule de profil, puis
 * retrouver la séance de l'autre côté. Ce crochet fait les deux d'un geste : il pose l'intention
 * « ouvrir le test d'entrée de la séance N », puis quitte l'espace parent comme
 * `ParentMainLayout.handleExitToChild` (`switchAccount` si jeton, puis `setIsParentMode(false)`).
 *
 * **L'intention passe par `pendingDeepLink`**, la file d'attente que la sortie de mode parent
 * possède déjà : `RootNavigator` la rejoue APRÈS le changement d'arbre de navigation, et sait
 * attendre le bon mode avant de naviguer. `pre-call` n'est enregistrée que dans la pile `main`
 * (`navigationScreens.js`), donc `needsModeSwitch("pre-call", true)` est vrai et la file
 * l'accepte — c'est le chemin d'une notification d'appel entrant
 * (`NotificationSubscriptionHandler.js`, cas `incoming-call`). Rien de nouveau n'est inventé.
 *
 * Le compte visé est celui de l'élève de la séance ; dans l'espace parent c'est le compte
 * CONNECTÉ (chaque chip enfant bascule déjà le compte, `handleUserSelection`), donc le cas
 * nominal n'a aucun compte à changer — et on ne le change pas : `SWITCH_ACCOUNT` viderait
 * `live_sessions`, soit exactement ce que l'écran d'entrée cherche pour retrouver la séance.
 *
 * Sans jeton dans la réserve, ou sous impersonation — où la bascule de profil est verrouillée
 * par le header (`profileDisabled`) — le crochet ne rend rien, et l'écran n'affiche pas la ligne.
 *
 * **Les sélecteurs rendent des PRIMITIFS.** L'accueil et « Tous mes lives » restent montés
 * (`unmountOnBlur: false`) : un sélecteur qui rend l'objet `currentUser` ou le tableau des jetons
 * re-rendait les deux écrans à chaque rafraîchissement du profil, alors que rien de ce qui est lu
 * ici n'avait bougé. Le compte visé, lui, n'est pas un état d'affichage — il se relit au moment
 * du clic, sur le magasin.
 */
export function useJoinLiveAsChild() {
    const dispatch = useDispatch()
    const store = useStore()
    const userId = useSelector((state) => state.data.currentUser?.object?.id)
    // Un booléen, pas les deux objets dont il se déduit : la règle reste dans `canExitToChild`,
    // et l'écran ne se redessine que quand la réponse change.
    const available = useSelector((state) =>
        canExitToChild(state.data.currentUser?.object, selectSessionStoredTokens(state))
    )

    const joinLiveAsChild = useCallback(
        (session) => {
            if (session?.liveSessionId == null) return
            const storedTokens = selectSessionStoredTokens(store.getState())
            const account = findStoredAccount(storedTokens, session?.studentId ?? userId)
            if (!account?.token) return

            // L'intention se pose AVANT la sortie : le réducteur `notifications` n'écoute ni
            // `SWITCH_ACCOUNT` ni le mode parent, elle traverse donc la bascule intacte.
            dispatch(
                setPendingDeepLink(
                    buildDeepLinkUrl(APP_URL, "pre-call", {
                        liveSessionId: session.liveSessionId,
                    })
                )
            )

            if (String(account.userId) !== String(userId)) dispatch(switchAccount(account))
            dispatch(setIsParentMode(false))
        },
        [dispatch, store, userId]
    )

    // Absent quand la bascule n'est pas possible : l'écran n'offre alors pas le geste. Le prénom,
    // lui, n'a pas à remonter d'ici — la machine des gestes le lit déjà sur le compte connecté.
    return { joinLiveAsChild: available ? joinLiveAsChild : undefined }
}
